'use client';

import { useEffect, useMemo, useState } from 'react';
import { Users, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { FieldHelp } from '@/features/guided-help';
import {
  wizardBlockCardClassName,
  wizardBlockShellClassName,
} from '../wizardBlockChrome';

export interface TeamsAllowedFieldProps {
  minTeamsAllowed: number;
  maxTeamsAllowed: number;
  onMinTeamsChange: (value: number) => void;
  onMaxTeamsChange: (value: number) => void;
  /** Lower bound for both fields (e.g. `teamsPerGroups`). Default 2. */
  minFloor?: number;
  wrapInCard?: boolean;
  minInputId?: string;
  maxInputId?: string;
  disabled?: boolean;
}

export function TeamsAllowedField({
  minTeamsAllowed,
  maxTeamsAllowed,
  onMinTeamsChange,
  onMaxTeamsChange,
  minFloor = 2,
  wrapInCard = true,
  minInputId = 'min-teams',
  maxInputId = 'max-teams',
  disabled = false,
}: TeamsAllowedFieldProps) {
  const [minTeamsInput, setMinTeamsInput] = useState(String(minTeamsAllowed));
  const [maxTeamsInput, setMaxTeamsInput] = useState(String(maxTeamsAllowed));

  useEffect(() => {
    setMinTeamsInput(String(minTeamsAllowed));
  }, [minTeamsAllowed]);

  useEffect(() => {
    setMaxTeamsInput(String(maxTeamsAllowed));
  }, [maxTeamsAllowed]);

  const parsedMinTeamsInput = useMemo(() => {
    const n = parseInt(minTeamsInput, 10);
    return Number.isFinite(n) ? n : null;
  }, [minTeamsInput]);

  const parsedMaxTeamsInput = useMemo(() => {
    const n = parseInt(maxTeamsInput, 10);
    return Number.isFinite(n) ? n : null;
  }, [maxTeamsInput]);

  const commitMinTeams = () => {
    if (parsedMinTeamsInput == null) {
      setMinTeamsInput(String(minTeamsAllowed));
      return;
    }
    onMinTeamsChange(Math.max(minFloor, parsedMinTeamsInput));
  };

  const commitMaxTeams = () => {
    if (parsedMaxTeamsInput == null) {
      setMaxTeamsInput(String(maxTeamsAllowed));
      return;
    }
    onMaxTeamsChange(Math.max(minFloor, parsedMaxTeamsInput));
  };

  const hasMinMaxError = minTeamsAllowed > maxTeamsAllowed;

  const body = (
    <>
      {hasMinMaxError && (
        <Alert variant="destructive" className="mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            El mínimo de equipos no puede ser mayor al máximo
          </AlertDescription>
        </Alert>
      )}
      <div className="grid gap-6 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={minInputId}>Mínimo de equipos</Label>
          <Input
            id={minInputId}
            type="number"
            min={minFloor}
            disabled={disabled}
            value={minTeamsInput}
            onChange={(e) => setMinTeamsInput(e.target.value)}
            onBlur={commitMinTeams}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                (e.currentTarget as HTMLInputElement).blur();
              }
            }}
          />
          <p className="text-xs text-gray-500">
            El torneo necesita al menos esta cantidad para iniciar
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor={maxInputId}>Máximo de equipos</Label>
          <Input
            id={maxInputId}
            type="number"
            min={minFloor}
            disabled={disabled}
            value={maxTeamsInput}
            onChange={(e) => setMaxTeamsInput(e.target.value)}
            onBlur={commitMaxTeams}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                (e.currentTarget as HTMLInputElement).blur();
              }
            }}
          />
          <p className="text-xs text-gray-500">
            Se cerrarán las inscripciones al alcanzar este número
          </p>
        </div>
      </div>
    </>
  );

  if (!wrapInCard) {
    return (
      <div
        className={wizardBlockShellClassName}
        data-guide-id="wizard.fixture.teamsAllowed"
      >
        {body}
      </div>
    );
  }

  return (
    <Card
      className={wizardBlockCardClassName}
      data-guide-id="wizard.fixture.teamsAllowed"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Users className="h-5 w-5" />
          Cantidad de equipos
          <FieldHelp topicId="wizard.fixture.teamsAllowed" />
        </CardTitle>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
